import { z } from 'zod';
import {
    CardPointeTerminalTokenizationOutcome,
    type CardPointeTerminalTokenizationErrorBody,
    type CardPointeTerminalTokenizationRequestBody,
    type CardPointeTerminalTokenizationRequestDetails,
    type CardPointeTerminalTokenizationResponseDetails,
    type CardPointeTerminalTokenizationResult,
    type CardPointeTerminalTokenizationSuccessBody,
} from '../../../types/ipg/cardpointe/cardpointe-terminal-tokenization.types';

export const CardPointeTerminalTokenizationAmountSchema: z.ZodString = z
    .string()
    .regex(/^\d+(?:\.\d{2})?$/);

export const CardPointeTerminalTokenizationRequestBodySchema: z.ZodObject<{
    merchantId: z.ZodString;
    hsn: z.ZodString;
    amount: z.ZodString;
    aid: z.ZodOptional<z.ZodEnum<{ debit: 'debit'; credit: 'credit' }>>;
    includeSignature: z.ZodOptional<z.ZodBoolean>;
    beep: z.ZodOptional<z.ZodBoolean>;
}> = z.object({
    merchantId: z.string().min(1),
    hsn: z.string().min(1),
    amount: CardPointeTerminalTokenizationAmountSchema,
    aid: z.enum(['debit', 'credit']).optional(),
    includeSignature: z.boolean().optional(),
    beep: z.boolean().optional(),
}) satisfies z.ZodType<CardPointeTerminalTokenizationRequestBody>;

export const CardPointeTerminalTokenizationRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'POST'>;
    path: z.ZodLiteral<'/api/v4/readCard'>;
    body: typeof CardPointeTerminalTokenizationRequestBodySchema;
}> = z.object({
    method: z.literal('POST'),
    path: z.literal('/api/v4/readCard'),
    body: CardPointeTerminalTokenizationRequestBodySchema,
}) satisfies z.ZodType<CardPointeTerminalTokenizationRequestDetails>;

export const CardPointeTerminalTokenizationSuccessBodySchema: z.ZodObject<{
    token: z.ZodString;
    expiry: z.ZodOptional<z.ZodString>;
    name: z.ZodOptional<z.ZodString>;
    singleUseToken: z.ZodOptional<z.ZodBoolean>;
    signature: z.ZodOptional<z.ZodUnknown>;
}> = z.object({
    token: z.string().min(1),
    expiry: z.string().min(1).optional(),
    name: z.string().min(1).optional(),
    singleUseToken: z.boolean().optional(),
    signature: z.unknown().optional(),
}) satisfies z.ZodType<CardPointeTerminalTokenizationSuccessBody>;

export const CardPointeTerminalTokenizationErrorBodySchema: z.ZodObject<{
    errorCode: z.ZodString;
    errorMessage: z.ZodString;
}> = z.object({
    errorCode: z.string().min(1),
    errorMessage: z.string().min(1),
}) satisfies z.ZodType<CardPointeTerminalTokenizationErrorBody>;

export const CardPointeTerminalTokenizationTokenCreatedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeTerminalTokenizationOutcome.TOKEN_CREATED>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CardPointeTerminalTokenizationSuccessBodySchema;
}> = z.object({
    outcome: z.literal(CardPointeTerminalTokenizationOutcome.TOKEN_CREATED),
    httpStatus: z.literal(200),
    body: CardPointeTerminalTokenizationSuccessBodySchema,
});

export const CardPointeTerminalTokenizationCanceledResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeTerminalTokenizationOutcome.CANCELED>;
    httpStatus: z.ZodNumber;
    body: typeof CardPointeTerminalTokenizationErrorBodySchema;
}> = z.object({
    outcome: z.literal(CardPointeTerminalTokenizationOutcome.CANCELED),
    httpStatus: z.number().int().positive(),
    body: CardPointeTerminalTokenizationErrorBodySchema,
});

export const CardPointeTerminalTokenizationUnsupportedTerminalResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeTerminalTokenizationOutcome.UNSUPPORTED_TERMINAL>;
    httpStatus: z.ZodNumber;
    body: typeof CardPointeTerminalTokenizationErrorBodySchema;
}> = z.object({
    outcome: z.literal(CardPointeTerminalTokenizationOutcome.UNSUPPORTED_TERMINAL),
    httpStatus: z.number().int().positive(),
    body: CardPointeTerminalTokenizationErrorBodySchema,
});

export const CardPointeTerminalTokenizationMalformedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeTerminalTokenizationOutcome.MALFORMED_RESPONSE>;
    httpStatus: z.ZodNumber;
    body: z.ZodUnknown;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CardPointeTerminalTokenizationOutcome.MALFORMED_RESPONSE),
    httpStatus: z.number().int().positive(),
    body: z.unknown(),
    message: z.string().min(1),
});

export const CardPointeTerminalTokenizationResponseDetailsSchema: z.ZodType<CardPointeTerminalTokenizationResponseDetails> =
    z.discriminatedUnion('outcome', [
        CardPointeTerminalTokenizationTokenCreatedResponseDetailsSchema,
        CardPointeTerminalTokenizationCanceledResponseDetailsSchema,
        CardPointeTerminalTokenizationUnsupportedTerminalResponseDetailsSchema,
        CardPointeTerminalTokenizationMalformedResponseDetailsSchema,
    ]);

export const CardPointeTerminalTokenizationTokenCreatedResultSchema: z.ZodObject<{
    status: z.ZodLiteral<typeof CardPointeTerminalTokenizationOutcome.TOKEN_CREATED>;
    gatewayToken: z.ZodString;
    expiry: z.ZodOptional<z.ZodString>;
    cardholderName: z.ZodOptional<z.ZodString>;
    singleUseToken: z.ZodOptional<z.ZodBoolean>;
}> = z.object({
    status: z.literal(CardPointeTerminalTokenizationOutcome.TOKEN_CREATED),
    gatewayToken: z.string().min(1),
    expiry: z.string().min(1).optional(),
    cardholderName: z.string().min(1).optional(),
    singleUseToken: z.boolean().optional(),
});

export const CardPointeTerminalTokenizationCanceledResultSchema: z.ZodObject<{
    status: z.ZodLiteral<typeof CardPointeTerminalTokenizationOutcome.CANCELED>;
    message: z.ZodString;
}> = z.object({
    status: z.literal(CardPointeTerminalTokenizationOutcome.CANCELED),
    message: z.string().min(1),
});

export const CardPointeTerminalTokenizationUnsupportedTerminalResultSchema: z.ZodObject<{
    status: z.ZodLiteral<typeof CardPointeTerminalTokenizationOutcome.UNSUPPORTED_TERMINAL>;
    message: z.ZodString;
}> = z.object({
    status: z.literal(CardPointeTerminalTokenizationOutcome.UNSUPPORTED_TERMINAL),
    message: z.string().min(1),
});

export const CardPointeTerminalTokenizationMalformedResultSchema: z.ZodObject<{
    status: z.ZodLiteral<typeof CardPointeTerminalTokenizationOutcome.MALFORMED_RESPONSE>;
    message: z.ZodString;
}> = z.object({
    status: z.literal(CardPointeTerminalTokenizationOutcome.MALFORMED_RESPONSE),
    message: z.string().min(1),
});

export const CardPointeTerminalTokenizationResultSchema: z.ZodType<CardPointeTerminalTokenizationResult> =
    z.discriminatedUnion('status', [
        CardPointeTerminalTokenizationTokenCreatedResultSchema,
        CardPointeTerminalTokenizationCanceledResultSchema,
        CardPointeTerminalTokenizationUnsupportedTerminalResultSchema,
        CardPointeTerminalTokenizationMalformedResultSchema,
    ]);
